'use client';

import { useAuth } from '@svenskaspelab/nova-core/nextjs/auth';
import { addMinutes, subMilliseconds } from 'date-fns';
import { useEffect, useRef } from 'react';

import { setSessionEndMessage } from '../../actions/userSession.js';
import { MAX_SESSION_TIME_MINUTES } from '../../constants.js';
import { useSessionTime } from '../../store/zustand.js';

const calculateSessionEndTime = (sessionTimeMs: number) => {
  const sessionEndTime = subMilliseconds(addMinutes(new Date(), MAX_SESSION_TIME_MINUTES), sessionTimeMs);
  return sessionEndTime;
};

export default function SessionEnd() {
  const { user, logout } = useAuth();
  const sessionEndTime = useSessionTime((state) => state.sessionEndTime);
  const setSessionEndTime = useSessionTime((state) => state.setSessionEndTime);
  const setSessionStartTime = useSessionTime((state) => state.setSessionStartTime);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (!user) {
      return;
    }
    const { sessionTimeMs } = user as Required<typeof user>;
    const sessionEnd = calculateSessionEndTime(sessionTimeMs);
    setSessionEndTime(sessionEnd);
    setSessionStartTime(subMilliseconds(new Date(), sessionTimeMs));
  }, [user, setSessionEndTime, setSessionStartTime]);

  useEffect(() => {
    if (!user) {
      return () => {};
    }
    clearTimeout(timeoutRef.current);
    const timeToEnd = sessionEndTime.getTime() - Date.now();

    // Typescript doesn't like promises in setTimeout
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    timeoutRef.current = setTimeout(async () => {
      await setSessionEndMessage();
      void logout();
    }, timeToEnd);
    return () => clearTimeout(timeoutRef.current);
  }, [sessionEndTime, logout, user]);

  return null;
}
